import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { getUser, isAdmin } from '@/lib/auth';
import { 
  Clock, MapPin, LogIn, LogOut, Coffee, Calendar, CalendarDays,
  CheckCircle, Loader2, User, LogOutIcon, AlertCircle,
  ChevronRight, GraduationCap, BookOpen, ClipboardList,
  Users, FileText, Save, Check, X, PenLine, ExternalLink,
  Calculator, Edit, Trash2, Plus, Bell, ArrowLeft
} from 'lucide-react';

// Dias da semana
const DAYS = [
  { id: 'segunda', label: 'Seg' },
  { id: 'terca', label: 'Ter' },
  { id: 'quarta', label: 'Qua' },
  { id: 'quinta', label: 'Qui' },
  { id: 'sexta', label: 'Sex' },
];

export default function EquipePortal() {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState('ponto');
  const [schoolLogo, setSchoolLogo] = useState(null);
  
  // Time records states
  const [todayRecords, setTodayRecords] = useState([]);
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  const [clockType, setClockType] = useState('entrada');
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Schedule states
  const [teacherSchedules, setTeacherSchedules] = useState([]);
  const [employee, setEmployee] = useState(null);
  
  // Professor states
  const [myAssignments, setMyAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Chamada states
  const [chamadaDialogOpen, setChamadaDialogOpen] = useState(false);
  const [chamadaDate, setChamadaDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});
  const [savingChamada, setSavingChamada] = useState(false);
  
  // Conteúdo states
  const [conteudoDialogOpen, setConteudoDialogOpen] = useState(false);
  const [conteudoDate, setConteudoDate] = useState(new Date().toISOString().split('T')[0]);
  const [conteudo, setConteudo] = useState('');
  const [tarefa, setTarefa] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [savingConteudo, setSavingConteudo] = useState(false);

  // Check if user is a teacher
  const isTeacher = user?.role === 'teacher' || user?.role === 'professor' || myAssignments.length > 0;

  useEffect(() => {
    if (!user) {
      navigate('/equipe');
      return;
    }
    
    loadData();
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load school logo
      try {
        const settingsRes = await api.get('/school-settings');
        if (settingsRes.data?.logo) {
          setSchoolLogo(settingsRes.data.logo);
        }
      } catch (e) {}
      
      // Load employee data
      const employeesRes = await api.get('/employees');
      const myEmployee = employeesRes.data.find(e => 
        e.email === user?.email || e.user_id === user?.id
      );
      setEmployee(myEmployee);
      
      // Load today's records
      if (myEmployee) {
        const recordsRes = await api.get(`/time-records?employee_id=${myEmployee.id}`);
        const today = new Date().toDateString();
        const todayRecs = recordsRes.data.filter(r => 
          new Date(r.timestamp).toDateString() === today
        );
        setTodayRecords(todayRecs);
        
        // Load teacher schedule by employee_id (from class_schedules)
        try {
          const schedulesRes = await api.get(`/class-schedules?employee_id=${myEmployee.id}`);
          setTeacherSchedules(schedulesRes.data || []);
        } catch (e) {
          console.log('Erro ao carregar horários:', e);
          setTeacherSchedules([]);
        }
      }
      
      // Load teacher assignments (turmas vinculadas) - filter by employee
      try {
        const assignmentsRes = await api.get('/teacher-assignments');
        // Filter by user email or employee
        const myAssigns = assignmentsRes.data.filter(a => 
          a.teacher_id === user?.id || 
          a.teacher_email === user?.email ||
          (myEmployee && a.teacher_id === myEmployee.id)
        );
        setMyAssignments(myAssigns);
      } catch (e) {
        setMyAssignments([]);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentsForAssignment = async (assignment) => {
    try {
      setLoadingStudents(true);
      const res = await api.get(`/students?class_group_id=${assignment.class_group_id}`);
      setStudents(res.data || []);
      
      // Initialize attendance data
      const initialAttendance = {};
      res.data.forEach(s => {
        initialAttendance[s.id] = 'presente';
      });
      setAttendanceData(initialAttendance);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoadingStudents(false);
    }
  };

  const getLocation = () => {
    setGettingLocation(true);
    setLocationError('');
    
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada pelo navegador');
      setGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
      },
      (error) => {
        let msg = 'Erro ao obter localização';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            msg = 'Permissão de localização negada';
            break;
          case error.POSITION_UNAVAILABLE:
            msg = 'Localização indisponível';
            break;
          case error.TIMEOUT:
            msg = 'Tempo esgotado';
            break;
        }
        setLocationError(msg);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleClockIn = async () => {
    if (!employee || !location) {
      toast.error('Dados incompletos');
      return;
    }
    
    try {
      setSubmitting(true);
      await api.post('/time-records', {
        employee_id: employee.id,
        record_type: clockType,
        latitude: location.latitude,
        longitude: location.longitude
      });
      
      toast.success('Ponto registrado!');
      setClockDialogOpen(false);
      setLocation(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar ponto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveChamada = async () => {
    if (!selectedAssignment) return;
    
    try {
      setSavingChamada(true);
      
      for (const [studentId, status] of Object.entries(attendanceData)) {
        await api.post('/student-attendance', {
          student_id: studentId,
          class_group_id: selectedAssignment.class_group_id,
          subject_id: selectedAssignment.subject_id,
          date: chamadaDate,
          status: status,
          teacher_id: user?.id
        });
      }
      
      toast.success('Chamada salva com sucesso!');
      setChamadaDialogOpen(false);
    } catch (error) {
      toast.error('Erro ao salvar chamada');
    } finally {
      setSavingChamada(false);
    }
  };

  const handleSaveConteudo = async () => {
    if (!selectedAssignment || !conteudo) {
      toast.error('Preencha o conteúdo');
      return;
    }
    
    try {
      setSavingConteudo(true);
      
      await api.post('/lesson-contents', {
        class_group_id: selectedAssignment.class_group_id,
        subject_id: selectedAssignment.subject_id,
        teacher_id: user?.id,
        date: conteudoDate,
        content: conteudo,
        homework: tarefa,
        observations: observacoes
      });
      
      toast.success('Conteúdo salvo!');
      setConteudoDialogOpen(false);
      setConteudo('');
      setTarefa('');
      setObservacoes('');
    } catch (error) {
      toast.error('Erro ao salvar conteúdo');
    } finally {
      setSavingConteudo(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('portal_equipe');
    navigate('/equipe');
  };

  const getClockTypeIcon = (type) => {
    switch (type) {
      case 'entrada': return <LogIn className="w-4 h-4 text-green-500" />;
      case 'saida': return <LogOut className="w-4 h-4 text-red-500" />;
      case 'saida_almoco': return <Coffee className="w-4 h-4 text-amber-500" />;
      case 'retorno_almoco': return <Coffee className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getClockTypeLabel = (type) => {
    switch (type) {
      case 'entrada': return 'Entrada';
      case 'saida': return 'Saída';
      case 'saida_almoco': return 'Saída Almoço';
      case 'retorno_almoco': return 'Retorno Almoço';
      default: return type;
    }
  };

  const getTodaySchedule = () => {
    const dayMap = { 1: 'segunda', 2: 'terca', 3: 'quarta', 4: 'quinta', 5: 'sexta' };
    const today = dayMap[currentTime.getDay()];
    return teacherSchedules
      .filter(s => s.day === today)
      .sort((a, b) => (a.time_slot || '').localeCompare(b.time_slot || ''));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const todaySchedule = getTodaySchedule();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-purple-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {schoolLogo ? (
                <img src={schoolLogo} alt="Logo" className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
              )}
              <div>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-purple-200 text-sm">
                  {isTeacher ? 'Professor(a)' : 'Funcionário(a)'}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-white/20"
            >
              <LogOutIcon className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs de navegação */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-4">
            <TabsTrigger value="ponto" className="gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Ponto</span>
            </TabsTrigger>
            <TabsTrigger value="horarios" className="gap-2">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Horários</span>
            </TabsTrigger>
            {isTeacher && (
              <>
                <TabsTrigger value="turmas" className="gap-2">
                  <GraduationCap className="w-4 h-4" />
                  <span className="hidden sm:inline">Turmas</span>
                </TabsTrigger>
                <TabsTrigger value="diario" className="gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">Diário</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Tab: Registro de Ponto */}
          <TabsContent value="ponto" className="space-y-4">
            {/* Current Time Card */}
            <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
              <CardContent className="py-6 text-center">
                <p className="text-purple-200 mb-2">
                  {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-5xl font-bold tracking-wider">
                  {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              </CardContent>
            </Card>

            {/* Clock In Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Registro de Ponto
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayRecords.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm text-muted-foreground font-medium">Registros de Hoje:</p>
                    <div className="flex flex-wrap gap-2">
                      {todayRecords.map(record => (
                        <Badge key={record.id} variant="outline" className="py-1.5 px-3">
                          {getClockTypeIcon(record.record_type)}
                          <span className="ml-2">{getClockTypeLabel(record.record_type)}</span>
                          <span className="ml-2 text-muted-foreground">
                            {new Date(record.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <Button 
                  size="lg" 
                  className="w-full h-14 text-lg bg-purple-600 hover:bg-purple-700"
                  onClick={() => {
                    setLocation(null);
                    setLocationError('');
                    setClockDialogOpen(true);
                  }}
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Registrar Ponto
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Horários */}
          <TabsContent value="horarios" className="space-y-4">
            {/* Today's Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  Aulas de Hoje
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todaySchedule.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhuma aula hoje</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {todaySchedule.map((schedule, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-purple-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary">{schedule.time_slot}</Badge>
                          <div>
                            <p className="font-medium">{schedule.subject_name}</p>
                            <p className="text-sm text-muted-foreground">{schedule.class_group_name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Week Schedule */}
            {teacherSchedules.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Quadro Semanal</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="p-2 text-left bg-purple-100">Horário</th>
                          {DAYS.map(day => (
                            <th key={day.id} className="p-2 text-center bg-purple-100">{day.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const slots = [...new Set(teacherSchedules.map(s => s.time_slot))].sort();
                          return slots.map((slot, idx) => (
                            <tr key={slot} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                              <td className="p-2 font-medium text-xs">{slot}</td>
                              {DAYS.map(day => {
                                const schedule = teacherSchedules.find(
                                  s => s.day === day.id && s.time_slot === slot
                                );
                                return (
                                  <td key={day.id} className="p-1 text-center">
                                    {schedule ? (
                                      <div className="bg-purple-100 rounded p-1.5 text-xs">
                                        <p className="font-medium truncate text-purple-900">
                                          {schedule.class_group_name || schedule.class_name || 'Turma'}
                                        </p>
                                        {schedule.subject_name && (
                                          <p className="text-purple-600 truncate text-[10px]">
                                            {schedule.subject_name}
                                          </p>
                                        )}
                                      </div>
                                    ) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab: Turmas (só para professores) */}
          {isTeacher && (
            <TabsContent value="turmas" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                    Minhas Turmas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myAssignments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nenhuma turma vinculada</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myAssignments.map((assignment, idx) => (
                        <div 
                          key={idx}
                          className="p-4 border rounded-lg hover:border-purple-300 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-semibold text-purple-900">{assignment.class_group_name}</p>
                              <p className="text-sm text-muted-foreground">{assignment.subject_name}</p>
                            </div>
                            <Badge>{assignment.year || '2026'}</Badge>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedAssignment(assignment);
                                loadStudentsForAssignment(assignment);
                                setChamadaDialogOpen(true);
                              }}
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              Chamada
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedAssignment(assignment);
                                setConteudoDialogOpen(true);
                              }}
                            >
                              <BookOpen className="w-4 h-4 mr-1" />
                              Conteúdo
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tab: Diário (só para professores) */}
          {isTeacher && (
            <TabsContent value="diario" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    Diário de Classe
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Selecione uma turma na aba "Turmas" para lançar chamada ou conteúdo.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4 text-center">
                        <ClipboardList className="w-8 h-8 mx-auto mb-2 text-green-600" />
                        <p className="font-medium text-green-900">Chamada</p>
                        <p className="text-sm text-green-700">Registrar presenças</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4 text-center">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                        <p className="font-medium text-blue-900">Conteúdo</p>
                        <p className="text-sm text-blue-700">Registrar aulas</p>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Dialog: Registrar Ponto */}
      <Dialog open={clockDialogOpen} onOpenChange={setClockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Ponto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={clockType} onValueChange={setClockType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">🟢 Entrada</SelectItem>
                  <SelectItem value="saida_almoco">🟡 Saída Almoço</SelectItem>
                  <SelectItem value="retorno_almoco">🔵 Retorno Almoço</SelectItem>
                  <SelectItem value="saida">🔴 Saída</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Localização GPS</Label>
              <div className="border rounded-lg p-4 bg-muted/30 text-center">
                {location ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span>Localização OK</span>
                  </div>
                ) : gettingLocation ? (
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" />
                ) : (
                  <MapPin className="w-8 h-8 mx-auto text-muted-foreground" />
                )}
                {locationError && (
                  <p className="text-red-500 text-sm mt-2">{locationError}</p>
                )}
              </div>
              {!location && (
                <Button onClick={getLocation} className="w-full" disabled={gettingLocation}>
                  <MapPin className="w-4 h-4 mr-2" />
                  {gettingLocation ? 'Capturando...' : 'Capturar Localização'}
                </Button>
              )}
            </div>

            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-purple-700">
                Horário: <strong>{currentTime.toLocaleTimeString('pt-BR')}</strong>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleClockIn} disabled={!location || submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Chamada */}
      <Dialog open={chamadaDialogOpen} onOpenChange={setChamadaDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Chamada - {selectedAssignment?.class_group_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input 
                type="date" 
                value={chamadaDate} 
                onChange={(e) => setChamadaDate(e.target.value)}
              />
            </div>
            
            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {loadingStudents ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : students.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Nenhum aluno</p>
              ) : (
                <div className="space-y-2">
                  {students.map(student => (
                    <div 
                      key={student.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted"
                    >
                      <span className="text-sm font-medium">{student.name}</span>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant={attendanceData[student.id] === 'presente' ? 'default' : 'outline'}
                          className={attendanceData[student.id] === 'presente' ? 'bg-green-600' : ''}
                          onClick={() => setAttendanceData(prev => ({ ...prev, [student.id]: 'presente' }))}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={attendanceData[student.id] === 'falta' ? 'default' : 'outline'}
                          className={attendanceData[student.id] === 'falta' ? 'bg-red-600' : ''}
                          onClick={() => setAttendanceData(prev => ({ ...prev, [student.id]: 'falta' }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChamadaDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveChamada} disabled={savingChamada}>
              {savingChamada ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Chamada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Conteúdo */}
      <Dialog open={conteudoDialogOpen} onOpenChange={setConteudoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Conteúdo - {selectedAssignment?.class_group_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input 
                type="date" 
                value={conteudoDate} 
                onChange={(e) => setConteudoDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Conteúdo da Aula *</Label>
              <Textarea 
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                placeholder="Descreva o conteúdo ministrado..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Tarefa de Casa</Label>
              <Textarea 
                value={tarefa}
                onChange={(e) => setTarefa(e.target.value)}
                placeholder="Tarefa para os alunos..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea 
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Observações gerais..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConteudoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveConteudo} disabled={savingConteudo || !conteudo}>
              {savingConteudo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="mt-8 py-4 text-center text-sm text-muted-foreground">
        <p>© 2026 Novo DNA Instituto de Aprendizagem</p>
      </footer>
    </div>
  );
}
